import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RecruiterSidebar from '../components/RecruiterSidebar';
import { useTheme } from '../context/ThemeContext';
import '../styles/theme.css';
import './SeekerProfileView.css';
import { API, MEDIA_URL } from '../config';

function fmt(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
}

export default function SeekerProfileView() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch(`${API}/job-seeker-profile/user/${userId}/`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async r => {
        if (r.ok) return r.json();
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${r.status}`);
      })
      .then(data => { setProfile(data); setLoading(false); })
      .catch(e => { setErrorMsg(e.message); setLoading(false); });
  }, [userId]);

  const photoUrl = profile?.profile_photo
    ? (profile.profile_photo.startsWith('http')
        ? profile.profile_photo
        : `${MEDIA_URL}${profile.profile_photo.startsWith('/') ? '' : '/'}${profile.profile_photo}`)
    : null;

  const handleMessage = () => {
    navigate('/recruiter/messages', { state: { startConvWith: parseInt(userId) } });
  };

  return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button className="topbar-btn" onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <span className="topbar-title">{profile?.full_name || 'Candidate Profile'}</span>
          </div>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          {loading ? (
            <div className="empty-state"><i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i></div>
          ) : !profile ? (
            <div className="empty-state">
              <i className="fas fa-user-slash"></i>
              <h3>Profile not found</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                {errorMsg || 'This candidate has not set up their profile yet.'}
              </p>
              <button className="btn btn-primary btn-sm" style={{ marginTop: '1rem' }} onClick={() => navigate(-1)}>
                Go Back
              </button>
            </div>
          ) : (
            <div className="spv-layout">
              {/* ── Left column ── */}
              <div className="spv-main">

                {/* Hero card */}
                <div className="card spv-hero">
                  <div className="spv-avatar-wrap">
                    {photoUrl
                      ? <img src={photoUrl} alt={profile.full_name} className="spv-avatar-img" />
                      : <div className="spv-avatar-placeholder"><i className="fas fa-user"></i></div>
                    }
                  </div>
                  <div className="spv-hero-info">
                    <h2 className="spv-name">{profile.full_name}</h2>
                    {profile.professional_title && <p className="spv-title">{profile.professional_title}</p>}
                    <div className="spv-meta-row">
                      {profile.city && <span><i className="fas fa-map-marker-alt"></i> {profile.city}{profile.province ? `, ${profile.province}` : ''}</span>}
                      {profile.employment_status && <span><i className="fas fa-briefcase"></i> {profile.employment_status}</span>}
                      {profile.years_of_experience != null && <span><i className="fas fa-clock"></i> {profile.years_of_experience} yr{profile.years_of_experience !== 1 ? 's' : ''} exp</span>}
                      {profile.notice_period && <span><i className="fas fa-calendar-check"></i> Available: {profile.notice_period}</span>}
                    </div>
                    <div className="spv-badge-row">
                      {profile.work_mode_preference && <span className="badge badge-blue"><i className="fas fa-laptop-house"></i> {profile.work_mode_preference}</span>}
                      {profile.willing_to_relocate && <span className="badge badge-green"><i className="fas fa-plane"></i> Open to relocate</span>}
                      {profile.linkedin_url && (
                        <a href={profile.linkedin_url} target="_blank" rel="noreferrer" className="badge badge-blue" style={{ textDecoration: 'none' }}>
                          <i className="fab fa-linkedin"></i> LinkedIn
                        </a>
                      )}
                      {profile.github_url && (
                        <a href={profile.github_url} target="_blank" rel="noreferrer" className="badge badge-gray" style={{ textDecoration: 'none' }}>
                          <i className="fab fa-github"></i> GitHub
                        </a>
                      )}
                      {profile.portfolio_url && (
                        <a href={profile.portfolio_url} target="_blank" rel="noreferrer" className="badge badge-purple" style={{ textDecoration: 'none' }}>
                          <i className="fas fa-globe"></i> Portfolio
                        </a>
                      )}
                    </div>
                  </div>
                  <button className="btn btn-primary spv-msg-btn" onClick={handleMessage}>
                    <i className="fas fa-comment"></i> Message
                  </button>
                </div>

                {/* About */}
                {profile.bio && (
                  <div className="card">
                    <h3 className="spv-section-title"><i className="fas fa-user"></i> About</h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '0.9rem' }}>{profile.bio}</p>
                  </div>
                )}

                {/* Work Experience */}
                {profile.work_experiences?.length > 0 && (
                  <div className="card">
                    <h3 className="spv-section-title"><i className="fas fa-briefcase"></i> Work Experience</h3>
                    <div className="spv-timeline">
                      {profile.work_experiences.map((w, i) => (
                        <div key={i} className="spv-timeline-item">
                          <div className="spv-timeline-dot"></div>
                          <div className="spv-tl-title">{w.job_title}</div>
                          <div className="spv-tl-company">{w.company_name}</div>
                          <div className="spv-tl-meta">
                            <i className="fas fa-calendar"></i>
                            {fmt(w.start_date)} — {w.is_current ? 'Present' : fmt(w.end_date)}
                            {w.location && <><i className="fas fa-map-marker-alt" style={{ marginLeft: '0.5rem' }}></i> {w.location}</>}
                          </div>
                          {w.description && <p className="spv-tl-desc">{w.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Education */}
                {profile.educations?.length > 0 && (
                  <div className="card">
                    <h3 className="spv-section-title"><i className="fas fa-graduation-cap"></i> Education</h3>
                    <div className="spv-timeline">
                      {profile.educations.map((e, i) => (
                        <div key={i} className="spv-timeline-item">
                          <div className="spv-timeline-dot"></div>
                          <div className="spv-tl-title">{e.degree} — {e.field_of_study}</div>
                          <div className="spv-tl-company">{e.institution_name}</div>
                          <div className="spv-tl-meta">
                            <i className="fas fa-calendar"></i>
                            {fmt(e.start_date)} — {e.is_current ? 'Present' : fmt(e.end_date)}
                            {e.grade && <><i className="fas fa-star" style={{ marginLeft: '0.5rem' }}></i> {e.grade}</>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Right column ── */}
              <div className="spv-sidebar">

                {/* Skills */}
                {profile.skills?.length > 0 && (
                  <div className="card">
                    <h3 className="spv-section-title"><i className="fas fa-code"></i> Skills</h3>
                    <div className="spv-skills-wrap">
                      {profile.skills.map((s, i) => (
                        <span key={i} className="spv-skill-tag">
                          {typeof s === 'string' ? s : s.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Job Preferences */}
                <div className="card">
                  <h3 className="spv-section-title"><i className="fas fa-sliders-h"></i> Preferences</h3>
                  <div className="spv-info-list">
                    {(profile.salary_min || profile.salary_max) && (
                      <div className="spv-info-row">
                        <i className="fas fa-money-bill-wave"></i>
                        <span>R{profile.salary_min?.toLocaleString()} – R{profile.salary_max?.toLocaleString()} / month</span>
                      </div>
                    )}
                    {profile.work_mode_preference && (
                      <div className="spv-info-row">
                        <i className="fas fa-laptop-house"></i>
                        <span>{profile.work_mode_preference}</span>
                      </div>
                    )}
                    {profile.notice_period && (
                      <div className="spv-info-row">
                        <i className="fas fa-calendar-check"></i>
                        <span>Available: {profile.notice_period}</span>
                      </div>
                    )}
                    {profile.willing_to_relocate != null && (
                      <div className="spv-info-row">
                        <i className="fas fa-plane"></i>
                        <span>{profile.willing_to_relocate ? 'Open to relocate' : 'Not relocating'}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Desired roles */}
                {profile.desired_job_titles?.length > 0 && (
                  <div className="card">
                    <h3 className="spv-section-title"><i className="fas fa-bullseye"></i> Looking For</h3>
                    <div className="spv-skills-wrap">
                      {profile.desired_job_titles.map((t, i) => (
                        <span key={i} className="spv-skill-tag spv-skill-tag--green">{t}</span>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
